import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowLeft, CheckCircle, AlertCircle, Eye, EyeOff, Sun, Moon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useTheme } from "../contexts/ThemeContext";

const API = (window.location.origin.includes('localhost') 
  ? process.env.REACT_APP_BACKEND_URL 
  : window.location.origin) + "/api";

const ResetPassword = () => {
  const { isDark, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("Invalid reset link. Please request a new password reset.");
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.email);
        } else {
          setError(response.data.error || "Invalid or expired reset link.");
        }
      } catch (err) {
        setError("Invalid or expired reset link. Please request a new password reset.");
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 transition-colors ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}>
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 border-t-indigo-500 mx-auto mb-4"></div>
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}>
      {/* Theme toggle in top right */}
      <button
        onClick={toggleTheme}
        className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'}`}
      >
        {isDark ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex flex-col items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_sales-scorecards/artifacts/8h0qwbfz_calldrip-logo-white%20%282%29.png" 
              alt="Calldrip" 
              className={`h-10 w-auto ${!isDark ? 'brightness-0' : ''}`}
            />
            <span className={`text-[10px] tracking-[0.2em] uppercase mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>scorecards</span>
          </div>
        </div>

        {/* Reset Password Card */}
        <div className={`rounded-2xl p-6 sm:p-8 shadow-xl transition-colors ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
          {success ? (
            // Success State
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                Password Reset!
              </h2>
              <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Your password has been successfully updated. You can now log in with your new password.
              </p>
              <Link to="/login">
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
                  Continue to Login
                </Button>
              </Link>
            </div>
          ) : !tokenValid ? (
            // Invalid Token State
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                Link Expired
              </h2>
              <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {error || "This password reset link is invalid or has expired."}
              </p>
              <Link to="/forgot-password">
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white mb-3">
                  Request New Reset Link
                </Button>
              </Link>
              <Link 
                to="/login"
                className={`text-sm transition-colors inline-flex items-center gap-1 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
              >
                <ArrowLeft size={14} />
                Back to login
              </Link>
            </div>
          ) : (
            // Form State
            <>
              <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                Set new password
              </h2>
              <p className={`mb-6 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Create a new password for <span className="font-medium text-indigo-400">{userEmail}</span>
              </p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : "text-slate-700"}>New Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`pr-10 focus:border-indigo-500 ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    Must be at least 6 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : "text-slate-700"}>Confirm Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`focus:border-indigo-500 ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5"
                  disabled={loading}
                  data-testid="reset-password-button"
                >
                  {loading ? (
                    <div className="loading-spinner w-5 h-5 border-t-white"></div>
                  ) : (
                    <>
                      <Lock className="mr-2 h-4 w-4" />
                      Reset Password
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
